import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, Grid, Stack, Typography} from '@mui/material';
import {AppDialog} from 'components/dialogs';
import {AppControlledInput} from 'components/form';
import {AppControlledEditor} from 'components/form/AppTextEditor';
import {AppInfoText} from 'components/typographies';
import {formatDuration} from 'global';
import {useCreateNote, useDebounceEffect, useGetNoteId, useSessionRedux, useUpdateNote} from 'hooks';
import {useTenantSettingHelper} from 'hooks/settings/useSetting';
import {NoteCreateUpdateRequest, noteDefaultValues, NoteResponse} from 'models/notes';
import {TenantSettingsKeysEnum} from 'models/settings';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';

interface AddUpdateReminderDialogProps {
    noteId?: string;
    open: boolean;
    onCloseDialog: (value?: NoteResponse) => void;
    afterCreateUpdateHandler: (value: NoteResponse) => void;
}

export const NotesAddUpdateDialog: React.FC<AddUpdateReminderDialogProps> = ({
    noteId,
    open,
    onCloseDialog,
    afterCreateUpdateHandler,
}) => {
    const {createNote, createNoteLoading, createNoteResponse} = useCreateNote();
    const {updateNote, updateNoteLoading, updateNoteResponse} = useUpdateNote();

    const {
        createNote: createNoteAutomatically,
        createNoteLoading: createNoteAutomaticallyLoading,
        createNoteResponse: createNoteAutomaticallyResponse,
    } = useCreateNote();
    const {
        updateNote: updateNoteAutomatically,
        updateNoteLoading: updateNoteAutomaticallyLoading,
        updateNoteResponse: updateNoteAutomaticallyResponse,
    } = useUpdateNote();

    const {getItemById, getItemByIdLoading, getItemByIdResponse} = useGetNoteId();
    const [autoSaveRenderSuccessMessage, setautoSaveRenderSuccessMessage] = useState(false);

    // Fix destructuring for Windows line endings

    const {tenantSettings} = useSelector(sessionSelector);

    const {isSettingEnabled} = useTenantSettingHelper();

    const isAutoSaveEnabledForNotesByTenantSetting = React.useMemo(
        () => isSettingEnabled([TenantSettingsKeysEnum.TenantEnableAutoSaveNotes.toString()]),
        [isSettingEnabled, tenantSettings],
    );

    const {setValue, control, handleSubmit, watch} = useForm<NoteCreateUpdateRequest>({
        defaultValues: {
            ...noteDefaultValues,
            title: isSettingEnabled([TenantSettingsKeysEnum.TenantEnableAutoSaveNotes.toString()]) ? 'Untitled' : '',
        },
    });
    // Get autosave delay in milliseconds from tenant settings (integerValue is in minutes)
    const autosaveDelayMs = React.useMemo(() => {
        const minutes = Number(
            tenantSettings?.find(
                setting =>
                    setting.uniqueId === TenantSettingsKeysEnum.TenantAutoSaveIntervalNotes &&
                    setting.value?.integerValue !== undefined,
            )?.value?.integerValue ?? 0,
        );
        return minutes > 0 ? minutes * 1000 : 0;
    }, [tenantSettings]);
    const description = watch('description');
    const watchTitle = watch('title');

    useDebounceEffect(
        () => {
            if (isAutoSaveEnabledForNotesByTenantSetting && autosaveDelayMs > 0) {
                handleSubmit(onSubmitHandlerForAutomaticSave)();
            }
        },
        [watchTitle, description, autosaveDelayMs],
        autosaveDelayMs,
    );
    useEffect(() => {
        if (noteId) {
            getItemById({variables: {id: noteId}});
        }
    }, [noteId]);

    useEffect(() => {
        if (getItemByIdResponse && !getItemByIdLoading) {
            setValue('title', getItemByIdResponse.title);
            setValue('id', getItemByIdResponse.id);
            setValue('description', getItemByIdResponse.description);
        }
    }, [getItemByIdResponse, getItemByIdLoading]);

    useEffect(() => {
        if (createNoteResponse && !createNoteLoading) {
            onCloseDialog(createNoteResponse);
        }
    }, [createNoteResponse, createNoteLoading, onCloseDialog]);

    useEffect(() => {
        if (updateNoteResponse && !updateNoteLoading) {
            onCloseDialog(updateNoteResponse);
        }
    }, [updateNoteResponse, updateNoteLoading, onCloseDialog]);

    useEffect(() => {
        if (createNoteAutomaticallyResponse && !createNoteAutomaticallyLoading && createNoteAutomaticallyResponse.id) {
            getItemById({variables: {id: createNoteAutomaticallyResponse.id}});
            setautoSaveRenderSuccessMessage(true);
            setTimeout(() => {
                setautoSaveRenderSuccessMessage(false);
            }, 2000);
            afterCreateUpdateHandler(createNoteAutomaticallyResponse);
        }
    }, [createNoteAutomaticallyResponse, createNoteAutomaticallyLoading, getItemById]);

    useEffect(() => {
        if (updateNoteAutomaticallyResponse && !updateNoteAutomaticallyLoading && updateNoteAutomaticallyResponse.id) {
            getItemById({variables: {id: updateNoteAutomaticallyResponse.id}});
            setautoSaveRenderSuccessMessage(true);
            setTimeout(() => {
                setautoSaveRenderSuccessMessage(false);
            }, 2000);
            afterCreateUpdateHandler(updateNoteAutomaticallyResponse);
        }
    }, [updateNoteAutomaticallyResponse, updateNoteAutomaticallyLoading, getItemById]);

    // Debounced autosave

    // Manual submit
    const onSubmitHandlerForAutomaticSave = (data: NoteCreateUpdateRequest) => {
        const id = data.id;
        delete data.id;
        if (isAutoSaveEnabledForNotesByTenantSetting) {
            // If autosave is enabled, use the appropriate mutation

            if (id) {
                updateNoteAutomatically({variables: {id: id, input: data}});
            } else {
                createNoteAutomatically({variables: {input: data}});
            }
        }
    };

    const onSubmitHandler = (data: NoteCreateUpdateRequest) => {
        const id = data.id;
        delete data.id;
        if (id) {
            updateNote({variables: {id: id, input: data}});
        } else {
            createNote({variables: {input: data}});
        }
    };

    return (
        <AppDialog
            open={open}
            onCloseDialog={onCloseDialog}
            title={
                <>
                    <Grid container spacing={1} flexDirection={'column'}>
                        <Grid item>
                            <Box display={'flex'} gap={1}>
                                {noteId ? 'Update' : 'Add'} Note
                                {autoSaveRenderSuccessMessage && (
                                    <>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <span>{'Saved'}</span>
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        </Grid>
                        {autosaveDelayMs > 0 && isAutoSaveEnabledForNotesByTenantSetting && (
                            <Grid item>
                                <AppInfoText>
                                    <>
                                        Autosave is enabled. Your changes will be saved automatically
                                        <b>after you stop typing</b> for {formatDuration(autosaveDelayMs)}.
                                    </>
                                </AppInfoText>
                            </Grid>
                        )}
                    </Grid>
                </>
            }
            isForm
            onFormSubmit={handleSubmit(onSubmitHandler)}
            footerProps={{
                submitButtonProps: {
                    isLoading: createNoteLoading || updateNoteLoading,
                    type: 'submit',
                    disabled: createNoteLoading || updateNoteLoading,
                },
                onCancelClick: () => {
                    onCloseDialog();
                },
            }}
        >
            {/* Only show autosave indicator if enabled */}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <AppControlledInput
                        label="Title"
                        placeholder="Enter note title"
                        name="title"
                        control={control}
                        rules={{required: 'Title is required'}}
                    />
                </Grid>
                <Grid item xs={12}>
                    <AppControlledEditor
                        style={{minHeight: 200}}
                        label="Description"
                        placeholder="Enter description"
                        name="description"
                        control={control}
                    />
                </Grid>
            </Grid>
        </AppDialog>
    );
};

export default NotesAddUpdateDialog;
